/**
 * useSystemGpuInfo hook
 *
 * Fetches system GPU information from GET /api/v1/system/gpu-info.
 * Returns backend type ('cuda' | 'mps' | 'rocm' | 'cpu') and vram_gb.
 *
 * Distinct from useGPUInfo (which calls /api/v1/animatediff/gpu):
 * this hook is used for platform guards such as disabling AnimateDiff
 * ComfyUI on macOS MPS where it is not supported.
 *
 * On API failure gpuInfo stays null and gpuError is set. Consumers MUST
 * distinguish null (unknown / detection failed) from real hardware values
 * before making capability decisions.
 */

import { useEffect, useState } from 'react';
import { apiClient } from '@/services/apiClient';

export type GpuBackend = 'cuda' | 'mps' | 'rocm' | 'cpu';

export interface SystemGpuInfo {
  backend: GpuBackend;
  /** VRAM in GB; may be null when the backend cannot determine the value. */
  vram_gb: number | null;
}

interface UseSystemGpuInfoResult {
  gpuInfo: SystemGpuInfo | null;
  loading: boolean;
  /** Set when the detection API call failed; gpuInfo will be null. */
  gpuError: boolean;
}

/**
 * Fetch GPU backend info once on mount.
 *
 * - gpuInfo is null while loading OR when detection failed.
 * - gpuError distinguishes a failed fetch from "still loading".
 * - Never fabricates fake hardware; callers that need a definite value must
 *   check gpuInfo !== null before using it.
 */
export function useSystemGpuInfo(): UseSystemGpuInfoResult {
  const [gpuInfo, setGpuInfo] = useState<SystemGpuInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [gpuError, setGpuError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchGpuInfo = async (): Promise<void> => {
      try {
        const res = await apiClient.get<SystemGpuInfo>('/api/v1/system/gpu-info');
        if (!cancelled) {
          setGpuInfo(res.data);
          setGpuError(false);
        }
      } catch {
        if (!cancelled) {
          // Do NOT fabricate a fallback — leave gpuInfo null so downstream
          // capability gates know hardware is unknown, not "CPU/0 GB".
          setGpuInfo(null);
          setGpuError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchGpuInfo();
    return () => {
      cancelled = true;
    };
  }, []);

  return { gpuInfo, loading, gpuError };
}
